import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@parentsquare/squarekit/components/ui/collapsible';
import React, { useState } from 'react';
import type { Citation } from '@/Public/ChatBot/Types';

interface CitationsListProps {
    readonly citations: readonly Citation[];
}

function fallbackTitle(url: string): string {
    try {
        const pathname = new URL(url).pathname;
        const lastSegment = pathname.split('/').filter(Boolean).pop() ?? '';
        if (!lastSegment) return new URL(url).hostname.replace(/^www\./, '');
        return decodeURIComponent(lastSegment)
            .replace(/[-_]/g, ' ')
            .replace(/\.[^.]+$/, '')
            .replace(/\b\w/g, (c) => c.toUpperCase());
    } catch {
        return url;
    }
}

export const CitationsList = ({ citations }: CitationsListProps): React.ReactNode => {
    const [open, setOpen] = useState(false);

    if (citations.length === 0) return null;

    return (
        <div className="tw:mt-2">
            <Collapsible open={open} onOpenChange={setOpen}>
                <CollapsibleTrigger
                    className="tw:flex tw:w-auto tw:cursor-pointer tw:items-center tw:gap-1.5 tw:rounded-none tw:border-none tw:bg-transparent tw:p-0 tw:text-sm tw:font-semibold tw:text-type-primary hover:tw:bg-transparent"
                >
                    <svg
                        viewBox="0 0 10 10"
                        fill="currentColor"
                        className={`tw:h-2.5 tw:w-2.5 tw:transition-transform ${open ? '' : 'tw:-rotate-90'}`}
                    >
                        <path d="M0 2h10L5 8z" />
                    </svg>
                    {`Show ${citations.length} source${citations.length !== 1 ? 's' : ''}`}
                </CollapsibleTrigger>
                <CollapsibleContent className="tw:overflow-hidden tw:p-0">
                    <ul className="tw:mt-1.5 tw:ml-4 tw:list-disc tw:space-y-1.5 tw:pl-4">
                        {citations.map((citation) => (
                            <li key={citation.source_document_link} className="tw:leading-tight">
                                <a
                                    href={citation.source_document_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="tw:text-xs tw:text-black tw:underline"
                                >
                                    {citation.source_document_title || fallbackTitle(citation.source_document_link)}
                                </a>
                            </li>
                        ))}
                    </ul>
                </CollapsibleContent>
            </Collapsible>
        </div>
    );
};
